/* eslint-disable no-unused-vars */
import React, { useContext, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  Autocomplete,
  Box, Button, CircularProgress, Divider, Grid, LinearProgress, Stack, TextField,
} from '@mui/material';
import moment from 'moment';
import { ExpandLessRounded, ExpandMoreRounded } from '@mui/icons-material';
import AlertContext from '../../../../Context/AlertContext';
import Elements from '../../../Elements';
import { assignCourier, getAdminRequestDetails, setRequestAsDone } from '../../../../Redux/Models/request';
import { getCategories, getConditions } from '../../../../Redux/Models/master';
import Images from '../../../../Configs/images';
import { getCourier } from '../../../../Redux/Models/courier';
import { getDigitalBox } from '../../../../Redux/Models/box';
import ConfirmationContext from '../../../../Context/ConfirmationContext';
import StepIcon from '../../../../Configs/steps';
import ROUTES from '../../../../Configs/routes';
import { getTotalItems } from '../../../../Utils/helpers';

export default function Component({
  selectedRequest: req, classes, error, message, isLoading,
}) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { requestId } = useParams();

  const { openAlert } = useContext(AlertContext);
  const { openConfirmation, closeConfirmation } = useContext(ConfirmationContext);

  const { couriers } = useSelector((state) => state.courier);

  const [choosenCourier, setChoosenCourier] = useState({ front_name: '', id: '' });
  const [activeStep, setActiveStep] = useState(1);
  const [hideTrackHistory, setHideTH] = useState(false);

  const getInitialData = () => {
    dispatch(getAdminRequestDetails(requestId));
    dispatch(getCourier({}));
    dispatch(getDigitalBox());
    dispatch(getCategories());
    dispatch(getConditions());
  };

  const historyDone = req?.tracking_history?.filter((item) => item?.title === 'Done');

  useEffect(() => {
    const courierOnIt = ['Waiting for Courier Acceptance', 'Courier On The Way', 'Courier Arrived at Pickup Place', 'Courier Arrived at Customer Place', 'Request going back to the warehouse'];
    if (courierOnIt.indexOf(req.status) > -1) {
      setActiveStep(3);
    } else if (req.status === 'Item Already Confirm by Customer') {
      setActiveStep(4);
    } else if (req.status === 'Done') {
      setActiveStep(5);
      openConfirmation({
        title: 'The request process has done',
        desc: 'You can back to request page',
        confirmText: 'Ok, go back',
        cancelText: 'Stay here',
        handleAgree: () => {
          navigate(ROUTES.request());
          closeConfirmation();
        },
        handleClose: () => {
          closeConfirmation();
        },
      });
    } else if (req.status === 'Arrived at Warehouse') {
      setActiveStep(4);
    }

    if (req.driver) {
      setChoosenCourier(req.driver);
    }
  }, [req]);

  useEffect(() => {
    if (!requestId) {
      navigate(-1);
    } else {
      getInitialData();
    }
  }, [requestId]);

  useEffect(() => {
    if (error) {
      openAlert({
        message: `Something went wrong. ${message}`,
        severity: 'error',
      });
    }
  }, [error]);

  const handleAssignCourier = async () => {
    await dispatch(assignCourier(requestId, { courier: choosenCourier.id }));
  };

  const updateStatusAsDone = (status) => {
    dispatch(setRequestAsDone(requestId));
  };

  const getImage = (name, step) => {
    if (step > activeStep) return StepIcon[`${name}Inactive`];
    return StepIcon[`${name}Active`];
  };

  const renderSubmitText = () => {
    switch (activeStep) {
      case 1: return 'Proceed Request';
      case 2: return 'Next';
      case 4: return 'Next';
      case 5: return 'Completed Request';
      default: return '';
    }
  };

  const wantToContinue = () => {
    switch (activeStep) {
      case 1: if (req.delivery_type?.icon === 'warehouse') {
        setActiveStep(4);
      } else {
        openConfirmation({
          title: 'Proceed Request Order',
          desc: 'You will proceed this request order to the next step! Make sure you read all the request detail information',
          confirmText: 'Confirm Proceed',
          handleAgree: () => {
            setActiveStep(2);
            closeConfirmation();
          },
          handleClose: () => {
            closeConfirmation();
          },
          direction: 'row-reverse',
          maxWidth: 'xs',
        });
      }
        break;
      case 2: openConfirmation({
        title: 'Confirm Courier Assigning',
        desc: 'You will assign courier to process this request order',
        confirmText: 'Confirm Assign',
        handleAgree: async () => {
          await handleAssignCourier();
          closeConfirmation();
        },
        handleClose: () => {
          closeConfirmation();
        },
        direction: 'row-reverse',
        maxWidth: 'xs',
      });
        break;
      case 4: setActiveStep(5);
        break;
      case 5: openConfirmation({
        title: 'Completing Request?',
        desc: 'Recheck again all the request detail before completing the request!',
        confirmText: 'Confirm',
        handleAgree: async () => {
          updateStatusAsDone();
          closeConfirmation();
        },
        handleClose: () => {
          closeConfirmation();
        },
        direction: 'row-reverse',
        maxWidth: 'xs',
      });
        break;
      default: setActiveStep(1);
    }
  };

  const renderCourierAssigning = () => (
    <Box
      boxShadow="2px 3px 6px rgba(0, 0, 0, 0.04), 0px 2px 8px rgba(0, 0, 0, 0.02)"
      border="1px solid #F5F6F8"
      bgcolor="#FDFDFD"
      borderRadius="10px"
      p={3}
      mt={3}
    >
      <Box fontWeight="700">Assigning Courier</Box>
      <Box mt={2}>
        <Box display="flex" gap={3}>
          <Box flex={1}>
            <label>Choose courier</label>
            <Autocomplete
              autoComplete
              disabled={activeStep !== 2}
              options={couriers}
              value={choosenCourier}
              onChange={(e, newVal) => setChoosenCourier({
                ...newVal,
                id: newVal.courier?.id,
                profile_picture: newVal.courier?.profile_picture,
                phone_number: newVal.courier?.phone_number,
              })}
              getOptionLabel={(opt) => opt.courier_name || ''}
              renderOption={(optProp, opt) => (
                <Box sx={{ px: 2, py: 1 }} {...optProp} className="flex gap-8 align-items-center link" style={{ gap: '8px !important' }} key={opt.courier?.id}>
                  <img src={opt.courier?.profile_picture} alt="profile_pict" width="40" height="40" className="courier-opt" style={{ borderRadius: 50 }} />
                  <div>
                    <div className="weight-700">{opt.courier_name}</div>
                    <div className="s-14 mute">{opt.courier?.id}</div>
                  </div>
                </Box>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Choose courier"
                  InputProps={{
                    ...params.InputProps,
                  }}
                  size="small"
                />
              )}
            />
            <div className="s-12 mute mt-2">
              <strong>{couriers.length}</strong>
              {' '}
              Available couriers
            </div>
          </Box>
          <Box flex={1} display={choosenCourier.id ? 'block' : 'none'}>
            <Box fontWeight="700" fontSize={14}>Courier Information</Box>
            <Box display="flex" mt={1} gap={2}>
              <Box flex={1.5} display="flex" gap={2}>
                <img src={choosenCourier.profile_picture} alt="courier" width="32" height="32" className="circle cover" />
                <Box>
                  <Box className="s-14 mute">Courier Name</Box>
                  <Box mt={0.5}>
                    <Box fontWeight="700" fontSize={14}>{choosenCourier.front_name || choosenCourier.courier_name}</Box>
                  </Box>
                </Box>
              </Box>
              <Box flex={1}>
                <Box className="s-14 mute">Phone Number</Box>
                <Box mt={0.5}>
                  <Box fontWeight="700" fontSize={14}>{choosenCourier.phone_number}</Box>
                </Box>
              </Box>
              <Box flex={1}>
                <Box className="s-14 mute">Courier ID</Box>
                <Box mt={0.5}>
                  <Box fontWeight="700" fontSize={14}>
                    {choosenCourier.id?.slice(0, 7)}
                    ...
                  </Box>
                </Box>
              </Box>
            </Box>
            {req.status === 'Waiting for Courier Acceptance' && activeStep !== 2 && (
              <div className="primary-green link s-14 weight-600 mt-2" onClick={() => setActiveStep(2)}>Change Courier</div>
            )}
          </Box>
        </Box>
      </Box>
      <Divider sx={{ my: 3 }} />

      <Box sx={{
        borderRadius: 2,
        border: '1px solid #DADADA',
        p: 2,
        display: req.driver ? 'block' : 'none',
      }}
      >
        <div className="flex space-between link" onClick={() => setHideTH(!hideTrackHistory)}>
          <Box fontWeight={700}>Courier Track</Box>
          {hideTrackHistory
            ? <ExpandMoreRounded fontSize="medium" />
            : <ExpandLessRounded fontSize="medium" />}
        </div>
        <div hidden={hideTrackHistory}>
          <div className="stepper mt-3">
            {req.tracking_history?.map((history, idx) => (
              <div className="flex gap-16 mt-2" key={history.timestamp}>
                <img src={idx === 0 ? Images.LinePointActive : Images.LinePoint} alt="line point" />
                <div className="flex space-between flex-1">
                  <div>
                    <div className="weight-700 mt-1 s-16">{history.title}</div>
                    <div className="s-14 mute mt-1">{history.description || '-'}</div>
                  </div>
                  <div className="s-14 mute">
                    {moment(new Date(history.timestamp)).format('HH.mm')}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </Box>
    </Box>
  );

  return (
    <Elements.AdminPageBase navigate={navigate}>
      <div className={classes.root}>
        {isLoading && <LinearProgress color="inherit" />}
        <Box fontWeight="700" fontSize={18} mt={2}>Detail Request Order</Box>
        <Box mt={4} className="flex align-items-center flex-center" gap={2}>
          <div className="center">
            <img height="40" width="40" src={getImage('RequestInformation', 1)} alt="Request in order data" />
            <div className={`mt-1 s-14 ${activeStep === 1 && 'weight-700 primary-green'}`}>Request Information</div>
          </div>
          <img src={Images.LongDivider} alt="LongDivider" />
          <div className="center">
            <img height="40" width="40" src={getImage('Courier', 2)} alt="Courier assign" />
            <div className={`mt-1 s-14 ${activeStep === 2 && 'weight-700 primary-green'}`}>Assigning Courier</div>
          </div>
          <img src={Images.LongDivider} alt="LongDivider" />
          <div className="center">
            <img height="40" width="40" src={getImage('TrackCourier', 3)} alt="Courier Track" />
            <div className={`mt-1 s-14 ${activeStep === 3 && 'weight-700 primary-green'}`}>Track Courier</div>
          </div>
          <img src={Images.LongDivider} alt="LongDivider" />
          <div className="center">
            <img height="40" width="40" src={getImage('CustomerValidating', 4)} alt="Customer Validating" />
            <div className={`mt-1 s-14 ${activeStep === 4 && 'weight-700 primary-green'}`}>Customer Validating</div>
          </div>
          <img src={Images.LongDivider} alt="LongDivider" />
          <div className="center">
            <img height="40" width="40" src={getImage('ConfirmRequest', 5)} alt="Confirmation" />
            <div className={`mt-1 s-14 ${activeStep === 5 && 'weight-700 primary-green'}`}>Confirm Request</div>
          </div>
        </Box>
        <Box
          boxShadow="2px 3px 6px rgba(0, 0, 0, 0.04), 0px 2px 8px rgba(0, 0, 0, 0.02)"
          border="1px solid #F5F6F8"
          bgcolor="#FDFDFD"
          borderRadius="10px"
          p={3}
          mt={4}
        >
          <Box display="flex" justifyContent="space-between" alignItems="flex-start">
            <Box display="inline-flex" flexDirection="column" alignItems="center">
              <img src={Images.OenoOut} alt="request in" width="28" height="28" />
              <div className="weight-600 s-12 mt-1 primary-green">
                Request Out
              </div>
            </Box>
            <Box textAlign="right">
              <Box display="inline-flex" gap={2}>
                <Box bgcolor="#D9EDFC" fontSize={14} fontWeight="700" color="#2391D9" py="2px" px={1} borderRadius="4px">{req.status}</Box>
                {!!req.is_partial
                && <Box bgcolor="#FFF5D9" fontSize={14} fontWeight="700" color="#000" py="2px" px={1} borderRadius="4px">Partial Request Out</Box>}
              </Box>
              <Box fontWeight="600" mt={1}>{req.id}</Box>
            </Box>
          </Box>
          <Divider sx={{ my: 3 }} />
          <Box display="flex">
            <Box flex={1}>
              <Box className="s-14 mute">Request Service</Box>
              <Box
                display="flex"
                gap={0.5}
                mt={1}
                alignItems="center"
              >
                <img alt="delivery" width="24" height="24" src={req.delivery_type?.icon === 'warehouse' ? Images.IconHome : Images.IconDelivery} />
                <Box fontWeight="700" fontSize={14}>{req.delivery_type?.name || 'Regular'}</Box>
              </Box>
            </Box>
            <Box flex={1}>
              <Box className="s-14 mute">Paid Status</Box>
              <Box mt={1}>
                <Box fontWeight="700" fontSize={14}>{req?.payment_status}</Box>
              </Box>
            </Box>
            <Box flex={1}>
              <Box className="s-14 mute">Pickup Date</Box>
              <Box mt={1}>
                <Box fontWeight="700" fontSize={14}>{moment(req.pickup_date).format('DD MMM YYYY, HH.mm')}</Box>
              </Box>
            </Box>
          </Box>
          <Divider sx={{ my: 3 }} />
          <Box>
            <Box fontWeight="700">Pickup Details</Box>
            <Box display="flex" mt={3} gap={4}>
              <Box bgcolor="#F5F6F8" border="1px solid #E5E5E5" borderRadius="5px" p={2} flex={1}>
                <Stack spacing={2} direction="column">
                  <Box>
                    <Box className="s-14 mute">Customer Name</Box>
                    <Box mt={0.5}>
                      <Box fontWeight="700" fontSize={14}>{req.pickup_address?.name}</Box>
                    </Box>
                  </Box>
                  <Box>
                    <Box className="s-14 mute">Phone Number</Box>
                    <Box mt={0.5}>
                      <Box fontWeight="700" fontSize={14}>
                        +
                        {req.pickup_address?.phone_number}
                      </Box>
                    </Box>
                  </Box>
                  <Box>
                    <Box className="s-14 mute">Pickup Address</Box>
                    <Box mt={0.5}>
                      <Box fontWeight="700" fontSize={14}>
                        {req.pickup_address?.address}
                        ,
                        {' '}
                        {req.pickup_address?.city}
                        ,
                        {' '}
                        {req.pickup_address?.province}
                        ,
                        {' '}
                        {req.pickup_address?.country}
                        ,
                        {' '}
                        {req.pickup_address?.postal_code}
                      </Box>
                    </Box>
                  </Box>
                </Stack>
              </Box>
              <Box py={2} flex={1}>
                <Box fontSize={14} fontWeight="700">Customer Information</Box>
                <Box mt={2}>
                  <Box className="s-14 mute">Customer ID</Box>
                  <Box mt={0.5}>
                    <Box fontWeight="700" fontSize={14}>{req.user?.id}</Box>
                  </Box>
                </Box>
                <Box mt={2}>
                  <Box className="s-14 mute">Subscription</Box>
                  <Box mt={0.5} display="flex" gap={1} alignItems="flex-start">
                    <img src={Images.IconSubscription} alt="subs" />
                    <Box>
                      <Box fontWeight="700" fontSize={14}>{req.user?.subscription}</Box>
                      <Box className="mute" fontSize={12} mt={0.5}>
                        Since
                        {' '}
                        {moment(req.user?.created_at).format('DD MMM YYYY, HH.mm')}
                      </Box>
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>
          <Divider sx={{ my: 3 }} />
          <Box>
            <Box fontWeight="700">Item Details</Box>
            <Box display="flex" mt={3} gap={3}>
              <Box flex={1}>
                <Box className="s-14 mute">Total Items</Box>
                <Box mt={0.5}>
                  <Box fontWeight="700" fontSize={14}>
                    {
                      getTotalItems(req)
                    }
                  </Box>
                </Box>
              </Box>
              <Box flex={1}>
                <Box className="s-14 mute">Notes</Box>
                <Box mt={0.5}>
                  <Box fontWeight="700" fontSize={14}>{req.pickup_notes || req.box?.notes || '-'}</Box>
                </Box>
              </Box>
            </Box>

            <Box mt={3}>
              <BoxBoxData
                data={req.box}
                aisle={{ name: req.aisle, column: req.column, row: req.row }}
              />
              <BoxItemData
                data={req.items}
                aisle={{ name: req.aisle, column: req.column, row: req.row }}
              />
            </Box>
          </Box>
          {
            historyDone?.length > 0 && (
              <>
                <Divider sx={{ my: 3 }} />
                <Box>
                  <Box fontWeight="700">Acceptence Detail</Box>
                  <Box display="flex" mt={3} gap={3}>
                    <Box flex={1}>
                      <Box className="s-14 mute">Photo</Box>
                      <Box mt={0.5}>
                        <img width="80" height="80" className="rounded cover" src={historyDone[0]?.photo || 'https://images.pexels.com/photos/2912108/pexels-photo-2912108.jpeg?auto=compress&cs=tinysrgb&dpr=1&w=500'} alt="courier" />
                      </Box>
                    </Box>
                    <Box flex={1}>
                      <Box className="s-14 mute">Description</Box>
                      <Box mt={0.5}>
                        <Box fontWeight="700" fontSize={14}>{historyDone[0]?.description || '-'}</Box>
                      </Box>
                    </Box>
                  </Box>
                </Box>
              </>
            )
          }
        </Box>

        {activeStep > 1 && req.delivery_type?.icon !== 'warehouse' && renderCourierAssigning()}

        <Box className="flex space-between gap-16 mt-4">
          <Button sx={{ px: 4 }} variant="outlined" className="action btn-outlined" disabled={activeStep > 2}>Decline Request</Button>
          <Button sx={{ px: 4, minWidth: 200 }} variant="contained" className="action btn-contained" onClick={wantToContinue} disabled={!renderSubmitText() || req.status === 'Done' || isLoading} startIcon={isLoading && (<CircularProgress size={18} sx={{ mr: 1, color: '#fff' }} />)}>{renderSubmitText() || 'Next'}</Button>
        </Box>
      </div>
    </Elements.AdminPageBase>
  );
}

export function BoxBoxData({ data, aisle }) {
  const [hide, setHide] = useState(false);
  const toggleHide = () => {
    setHide(!hide);
  };

  return data?.length && (
    <div
      style={{
        borderRadius: 8, width: 'calc(100% - 34px)', border: '1px solid #DADADA', padding: 16,
      }}
      className="mt-3"
    >
      <div className="flex space-between align-items-center link" onClick={toggleHide}>
        <h4>
          Box
        </h4>
        {hide
          ? <ExpandMoreRounded fontSize="medium" />
          : <ExpandLessRounded fontSize="medium" />}
      </div>
      <div hidden={hide}>
        <Grid container mt={3} spacing={2}>
          {
          data?.map((box) => (
            <Grid item sm={6} key={box?.id}>
              <Box display="flex" gap={1.5} alignItems="flex-start">
                <img width="80" height="80" className="rounded cover" src={box?.photo || 'https://images.pexels.com/photos/2912108/pexels-photo-2912108.jpeg?auto=compress&cs=tinysrgb&dpr=1&w=500'} alt={box?.name} />
                <Box flex={1}>
                  <div className="weight-600">
                    {box?.name || `Box ${box?.digital_box_id}`}
                  </div>
                  <Box>
                    {box?.amount}
                    {' '}
                    {box?.amount > 1 ? 'Items' : 'Item'}
                  </Box>
                  <div className="s-10" color="#586479">
                    Aisle Name:
                    {' '}
                    {aisle?.name}
                    <br />
                    Column:
                    {' '}
                    {aisle?.column}
                    <br />
                    Row:
                    {' '}
                    {aisle?.row}
                  </div>
                </Box>
              </Box>
            </Grid>
          ))
        }
        </Grid>
      </div>
    </div>
  );
}

export function BoxItemData({ data, aisle }) {
  const [hide, setHide] = useState(false);
  const toggleHide = () => {
    setHide(!hide);
  };

  return data?.length && (
    <div
      style={{
        borderRadius: 8,
        border: '1px solid #DADADA',
        padding: 16,
        width: 'calc(100% - 34px)',
      }}
      className="mt-3"
    >
      <div className="flex space-between align-items-center link" onClick={toggleHide}>
        <h4>
          Item
        </h4>
        {hide
          ? <ExpandMoreRounded fontSize="medium" />
          : <ExpandLessRounded fontSize="medium" />}
      </div>
      <div hidden={hide}>
        <Grid container mt={3} spacing={2}>
          {
          data?.map((item) => (
            <Grid item sm={6} key={item?.id}>
              <Box display="flex" gap={1.5} alignItems="flex-start">
                <img width="80" height="80" className="rounded cover" src={item?.photo || 'https://images.pexels.com/photos/2912108/pexels-photo-2912108.jpeg?auto=compress&cs=tinysrgb&dpr=1&w=500'} alt={item.name} />
                <Box flex={1}>
                  <div className="weight-600">{item?.name}</div>
                  <Box display="flex" className="s-10" color="#586479" gap={0.5} flexWrap="wrap" alignItems="center">
                    <div>{item.category?.name || '-'}</div>
                    <div>
                      ・
                      {item.condition?.name || '-'}
                    </div>
                    <div>
                      ・
                      {item?.volume}
                    </div>
                  </Box>
                  <div className="s-10" color="#586479">
                    Aisle Name:
                    {' '}
                    {aisle?.name}
                    <br />
                    Column:
                    {' '}
                    {aisle?.column}
                    <br />
                    Row:
                    {' '}
                    {aisle?.row}
                  </div>
                </Box>
              </Box>
            </Grid>
          ))
        }
        </Grid>
      </div>
    </div>
  );
}
